import React from 'react';

import CustomModal from 'components/CustomModal';
import { Button } from 'components/Button';

import { FolderPath } from 'containers/Documents/components/FolderTreePicker/styles';
import { Description, ModalContent, PathValue } from 'containers/Documents/components/MoveToFolderModal/styles';

import { OrgUnit, findUnit, isIntake, parentOf, parentsWithNameOf, subtreeIds, unitPath } from '../types';

import UnitTreePicker from './UnitTreePicker';
import { listJoin, plural } from './contents';
import { DialogGroup } from './styles';
import { useDialogExpand } from './useUnitTreeView';

export interface MoveUnitModalProps {
  open: boolean;
  units: OrgUnit[];
  unitId: string | null;
  /** The page tree's expand state, copied when the dialog opens. */
  expandedIds: Set<string>;
  onCancel: () => void;
  onConfirm: (newParentId: string) => Promise<void>;
}

const MoveUnitModal: React.FunctionComponent<MoveUnitModalProps> = ({
  open,
  units,
  unitId,
  expandedIds: pageExpandedIds,
  onCancel,
  onConfirm,
}) => {
  const currentParentId = unitId ? (parentOf(units, unitId)?.uuid ?? null) : null;
  const [parentId, setParentId] = React.useState<string | null>(currentParentId);
  const { expandedIds, toggleExpand } = useDialogExpand(open, pageExpandedIds);

  React.useEffect(() => {
    if (open) setParentId(currentParentId);
    // Only on opening: currentParentId is derived from the tree, which gets a new identity on
    // every reload, and a reset would throw away the destination already chosen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const unit = unitId ? findUnit(units, unitId) : null;
  const moving = unitId ? subtreeIds(units, unitId) : [];
  const unitsBeneath = Math.max(moving.length - 1, 0);
  const movingSet = new Set(moving);
  const countPeople = (list: OrgUnit[]): number =>
    list.reduce(
      (total, node) => total + (movingSet.has(node.uuid) ? node.userCount : 0) + countPeople(node.children),
      0
    );
  const people = countPeople(units);

  const disabledReasons = new Map<string, string>();
  if (unit) {
    moving.forEach((id) =>
      disabledReasons.set(id, id === unit.uuid ? "A unit can't move under itself" : `This unit is under ${unit.name}`)
    );
    parentsWithNameOf(units, unit.uuid).forEach((id) =>
      disabledReasons.set(id, `Already has a unit called ${unit.name}`)
    );
    const intake = units.flatMap((top) => top.children).find(isIntake);
    if (intake) disabledReasons.set(intake.uuid, 'Intake only holds records waiting to be placed');
  }

  // Records do not carry a unit yet, so the only thing a move changes is where people sit.
  const movesWith = [
    unitsBeneath > 0 ? plural(unitsBeneath, 'unit', 'units') : null,
    people > 0 ? plural(people, 'person', 'people') : null,
  ].filter((part): part is string => part !== null);

  return (
    <CustomModal
      openModal={open}
      onClose={onCancel}
      modalTitle="Move unit"
      modalWidth="sm"
      cancelButton={
        <Button onClick={onCancel} variant="outlined" size="large">
          Cancel
        </Button>
      }
      submitButton={
        <Button
          onClick={() => parentId && onConfirm(parentId)}
          disabled={!parentId || parentId === currentParentId}
          variant="contained"
          size="large"
        >
          Move
        </Button>
      }
    >
      <ModalContent data-testid="move-unit-dialog">
        <Description>Select a destination unit for &quot;{unit?.name}&quot;</Description>
        {movesWith.length ? (
          <DialogGroup>
            <FolderPath>{`This also moves ${listJoin(movesWith)}.`}</FolderPath>
          </DialogGroup>
        ) : null}
        <UnitTreePicker
          units={units}
          selectedId={parentId}
          expandedIds={expandedIds}
          onSelect={setParentId}
          onToggleExpand={toggleExpand}
          disabledReasons={disabledReasons}
        />
        {parentId && unit ? (
          <FolderPath>
            New unit path: <PathValue>{`${unitPath(units, parentId)} / ${unit.name}`}</PathValue>
          </FolderPath>
        ) : null}
      </ModalContent>
    </CustomModal>
  );
};

export default MoveUnitModal;
